import { onValue, ref } from 'firebase/database'
import { getFirebaseServices } from '../../services/firebase'
import {
  resetVisibility,
  roleChangeObserver,
  subscribeVisibility,
} from '../../services/visibility-cache'

import { useQueryClient } from '@tanstack/react-query'
import {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import {
  firebaseAuthAdapter,
  type AuthAdapter,
  type DashboardUser,
} from './auth-adapter'

type AuthState =
  | { status: 'hydrating'; user: null }
  | { status: 'unauthenticated'; user: null }
  | { status: 'authenticated'; user: DashboardUser }

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<void>
  sendPasswordReset: (email: string, continueUrl?: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<DashboardUser>
  sendVerification: () => Promise<void>
  reloadCurrentUser: () => Promise<DashboardUser>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({
  children,
  adapter = firebaseAuthAdapter,
}: {
  children: ReactNode
  adapter?: AuthAdapter
}) {
  const queryClient = useQueryClient()
  const [visibilityGeneration, setVisibilityGeneration] = useState(0)
  useEffect(
    () =>
      subscribeVisibility(() => {
        queryClient.clear()
        setVisibilityGeneration((value) => value + 1)
      }),
    [queryClient]
  )
  const previousUid = useRef<string | null>(null)
  const [state, setState] = useState<AuthState>({
    status: 'hydrating',
    user: null,
  })

  useEffect(
    () =>
      adapter.subscribe((user) => {
        if (previousUid.current && previousUid.current !== user?.uid) {
          resetVisibility()
          queryClient.clear()
        }
        previousUid.current = user?.uid ?? null
        setState(
          user
            ? { status: 'authenticated', user }
            : { status: 'unauthenticated', user: null }
        )
      }),
    [adapter, queryClient]
  )

  useEffect(() => {
    if (adapter !== firebaseAuthAdapter || state.status !== 'authenticated')
      return
    const observe = roleChangeObserver(resetVisibility)
    return onValue(
      ref(getFirebaseServices().database, `Users/${state.user.uid}/role`),
      (snapshot) => observe(snapshot.val()),
      () => observe(null)
    )
  }, [adapter, state.status, state.user?.uid])

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      async login(email, password) {
        await adapter.signIn(email, password)
        queryClient.clear()
      },
      async sendPasswordReset(email, continueUrl) {
        if (!adapter.sendPasswordReset) {
          throw new Error('Password reset is unavailable')
        }
        await adapter.sendPasswordReset(email, continueUrl)
      },
      async signUp(email, password) {
        const user = await adapter.signUp(email, password)
        setState({ status: 'authenticated', user })
        previousUid.current = user.uid
        return user
      },
      sendVerification: () => adapter.sendEmailVerification(),
      async reloadCurrentUser() {
        const user = await adapter.reloadCurrentUser()
        setState({ status: 'authenticated', user })
        previousUid.current = user.uid
        return user
      },
      async logout() {
        await adapter.signOut()
        queryClient.clear()
      },
    }),
    [adapter, queryClient, state]
  )

  return (
    <AuthContext.Provider value={value}>
      <Fragment key={visibilityGeneration}>{children}</Fragment>
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
